"use client";

import { useEffect, useState } from "react";

export interface AxisValue {
  value: string;
  label?: string;
  code?: string;
  image?: string;
  available?: boolean;
  subtitle?: string;
}
export interface VariantAxis {
  key: string;
  label: string;
  kind: string; // "color" | "option"
  hasImages: boolean;
  values: AxisValue[];
}
export interface V3Variant {
  variantId: string;
  options: Record<string, string>;
  stock?: number;
  available?: boolean;
}

interface Props {
  axes: VariantAxis[];
  variants: V3Variant[];
  selectedVariantId: string | null;
  onSelect: (variantId: string) => void;
  /** Fires with the picked colour's photo (or null) so the parent can swap the hero image. */
  onImageChange?: (image: string | null) => void;
}

// Fallback swatch colours when the seller didn't set a hex code (or it's the default black).
const COLOR_HEX: Record<string, string> = {
  black: "#1f2937", white: "#f1f0ec", navy: "#1e3a8a", red: "#dc2626", blue: "#2563eb",
  brown: "#92400e", green: "#16a34a", grey: "#6b7280", gray: "#6b7280", pink: "#ec4899",
  purple: "#7c3aed", teal: "#0d9488", beige: "#d8c3a5", yellow: "#eab308", orange: "#f97316",
  silver: "#c0c0c0", gold: "#d4af37", maroon: "#7f1d1d", wood: "#a47551", multi: "#9ca3af",
};
const swatchColor = (val: AxisValue): string => {
  if (val.code && val.code.toLowerCase() !== "#000000") return val.code;
  return COLOR_HEX[String(val.value || "").trim().toLowerCase()] || val.code || "#CFCAC0";
};

// Playful kid avatars + pastel backgrounds for kids-footwear size buttons (decorative).
const KID_AVATARS = ["🧒", "👦", "👧", "🧒", "👶", "👦", "👧"];
const AVATAR_BG = ["#EAE7FB", "#FDE8E4", "#E4F0FB", "#FBEEF5", "#E9F6EE"];

// The size subtitle is category-driven: "3-4 Yrs" (kids age), "26 x 26 cm"
// (decor dimensions), "Chest 38 in" (apparel fit), "UK 7 / US 8" (shoe conversion).
// Only an age subtitle gets the playful kid avatar — everything else renders as
// plain stacked text so a wall clock never shows a child emoji.
const isAgeSubtitle = (s?: string) => {
  if (!s) return false;
  const t = s.trim();
  // "3-4 Yrs", "6-9 months", "0-3 mo" → age vocabulary; "0-3 m" → infant months
  // (bare m from the EU table). "26 x 26 cm" / "Chest 38 in" deliberately don't match.
  return /\b(yrs?|years?|months?|mos?)\b/i.test(t) || /\d\s*m$/i.test(t);
};

/**
 * Structured variant selector: one row per dimension — Color as swatches,
 * Size / Width / Age / Scent / anything as buttons. When every axis has a
 * value chosen, the matching variant's id is sent to the parent. Availability
 * cross-filters, so picking a colour greys out sizes not stocked in it.
 */
export function VariantAxesSelector({ axes, variants, selectedVariantId, onSelect, onImageChange }: Props) {
  const [sel, setSel] = useState<Record<string, string>>({});

  // A colour pick swaps the hero image immediately — before the rest of the
  // axes (size, etc.) are chosen and a full variant resolves.
  useEffect(() => {
    if (!onImageChange) return;
    const colorAxis = axes.find((a) => a.kind === "color");
    if (!colorAxis) return;
    const picked = colorAxis.values.find((v) => v.value === sel[colorAxis.key]);
    onImageChange(picked?.image || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, axes]);

  // Notify parent once a full combination is chosen.
  useEffect(() => {
    if (axes.length === 0 || !axes.every((a) => sel[a.key])) return;
    const match = variants.find((v) => axes.every((a) => v.options[a.key] === sel[a.key]));
    if (match && match.variantId !== selectedVariantId) onSelect(match.variantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, axes, variants]);

  const valueAvailable = (axisKey: string, value: string) =>
    variants.some(
      (v) =>
        v.options[axisKey] === value &&
        (v.available ?? (v.stock ?? 0) > 0) &&
        Object.entries(sel).every(([k, val]) => k === axisKey || v.options[k] === val)
    );

  return (
    <div className="flex flex-col gap-4">
      {axes.map((axis) => {
        const isColor = axis.kind === "color";
        return (
          <div key={axis.key} className="flex flex-col gap-2">
            <p className="text-[12px] font-bold uppercase tracking-wider text-[#8A8A85]">
              {axis.label}
              {sel[axis.key] && (
                <span className="ml-1.5 font-semibold normal-case text-[#1D1D1D]">{sel[axis.key]}</span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {axis.values.map((val, i) => {
                const selected = sel[axis.key] === val.value;
                const available = valueAvailable(axis.key, val.value);

                if (isColor) {
                  return (
                    <button
                      key={val.value}
                      type="button"
                      disabled={!available}
                      title={val.value}
                      onClick={() => setSel((s) => ({ ...s, [axis.key]: val.value }))}
                      className="relative h-12 w-12 overflow-hidden rounded-full border-2 transition disabled:opacity-40"
                      style={{
                        borderColor: selected ? "#F26250" : "#E8E3DA",
                        filter: available ? "none" : "grayscale(1)",
                      }}
                    >
                      {val.image ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={val.image} alt={val.value} className="h-full w-full object-cover" />
                      ) : (
                        <span className="block h-full w-full" style={{ background: swatchColor(val) }} />
                      )}
                    </button>
                  );
                }

                return (
                  <button
                    key={val.value}
                    type="button"
                    disabled={!available}
                    aria-pressed={selected}
                    onClick={() => setSel((s) => ({ ...s, [axis.key]: val.value }))}
                    className={`rounded-xl border-2 font-semibold transition disabled:cursor-not-allowed ${val.subtitle ? "px-3 py-2" : "px-4 py-2 text-[13px]"}`}
                    style={{
                      borderColor: selected ? "#F26250" : !available ? "#EAE7E0" : "#E8E3DA",
                      background: selected ? "#FEF6F4" : available ? "#fff" : "#F4F3F0",
                      color: !available ? "#A8A39B" : selected ? "#D94A39" : "#1D1D1D",
                      opacity: available ? 1 : 0.55,
                      filter: available ? "none" : "grayscale(1)",
                    }}
                  >
                    {val.subtitle ? (
                      isAgeSubtitle(val.subtitle) ? (
                        <span className="flex min-w-[58px] flex-col items-center gap-1 leading-tight">
                          <span
                            className="flex h-8 w-8 items-center justify-center rounded-full text-[16px]"
                            style={{ background: AVATAR_BG[i % AVATAR_BG.length] }}
                            aria-hidden
                          >
                            {KID_AVATARS[i % KID_AVATARS.length]}
                          </span>
                          <span className="text-[13px] font-bold">{val.label || val.value}</span>
                          <span className="text-[10px] font-medium text-[#8A8A85]">{val.subtitle}</span>
                        </span>
                      ) : (
                        <span className="flex min-w-[48px] flex-col items-center gap-0.5 leading-tight">
                          <span className="text-[13px] font-bold">{val.label || val.value}</span>
                          <span className="text-[10px] font-medium text-[#8A8A85]">{val.subtitle}</span>
                        </span>
                      )
                    ) : (
                      val.label || val.value
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
